import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, X, Save, Upload, Sparkles, Loader2, RefreshCw, Check } from 'lucide-react';
import { PAPER_BASE } from '../config';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const CATEGORIES = [
  { value: 'stage', label: '무대' },
  { value: 'chair', label: '의자' },
  { value: 'booth', label: '부스' },
  { value: 'banner', label: '배너' },
  { value: 'tent', label: '텐트' },
  { value: 'table', label: '테이블' },
  { value: 'fence', label: '펜스' },
  { value: 'general', label: '기타' },
];

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));

export default function PaperAdminAssetPage() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'general', widthMeter: '', heightMeter: '', color: '#6366f1' });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  // AI 생성
  const [showAiForm, setShowAiForm] = useState(false);
  const [aiDesc, setAiDesc] = useState('');
  const [aiForm, setAiForm] = useState({ name: '', category: 'general', widthMeter: '2', heightMeter: '2' });
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPreview, setAiPreview] = useState(null); // { imageUrl, s3Key }
  const [aiConfirming, setAiConfirming] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAssets = () => {
    setLoading(true);
    axios.get(`${API}/api/event-layout/assets`, { headers })
      .then(res => setAssets(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAssets(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm({ name: '', category: 'general', widthMeter: '', heightMeter: '', color: '#6366f1' });
    setImageFile(null);
    setImagePreview(null);
    setShowForm(true);
  };

  const openEdit = (asset) => {
    setEditId(asset.id);
    setForm({ name: asset.name, category: asset.category, widthMeter: asset.widthMeter, heightMeter: asset.heightMeter, color: asset.color || '#6366f1' });
    setImageFile(null);
    setImagePreview(asset.imageUrl);
    setShowForm(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.widthMeter || !form.heightMeter) return alert('이름, 가로, 세로를 입력해주세요.');
    setSaving(true);

    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('category', form.category);
    fd.append('widthMeter', form.widthMeter);
    fd.append('heightMeter', form.heightMeter);
    fd.append('color', form.color);
    if (imageFile) fd.append('image', imageFile);

    try {
      if (editId) {
        await axios.put(`${API}/api/event-layout/assets/${editId}`, fd, { headers });
      } else {
        await axios.post(`${API}/api/event-layout/assets`, fd, { headers });
      }
      setShowForm(false);
      fetchAssets();
    } catch {
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" 에셋을 삭제하시겠습니까?`)) return;
    try {
      await axios.delete(`${API}/api/event-layout/assets/${id}`, { headers });
      fetchAssets();
    } catch {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // AI: 미리보기 생성
  const handleAiGenerate = async () => {
    if (!aiDesc) return alert('에셋 설명을 입력해주세요.');
    // 이전 미리보기가 있으면 삭제
    if (aiPreview?.s3Key) {
      axios.post(`${API}/api/event-layout/assets/generate-discard`, { s3Key: aiPreview.s3Key }, { headers }).catch(() => {});
    }
    setAiGenerating(true);
    setAiPreview(null);
    try {
      const res = await axios.post(`${API}/api/event-layout/assets/generate-preview`, { description: aiDesc }, { headers });
      setAiPreview(res.data);
      if (!aiForm.name) setAiForm(f => ({ ...f, name: aiDesc.slice(0, 30) }));
    } catch (err) {
      alert(err.response?.data?.message || 'AI 생성 중 오류가 발생했습니다.');
    } finally {
      setAiGenerating(false);
    }
  };

  // AI: 확정
  const handleAiConfirm = async () => {
    if (!aiPreview || !aiForm.name) return alert('이름을 입력해주세요.');
    setAiConfirming(true);
    try {
      await axios.post(`${API}/api/event-layout/assets/generate-confirm`, {
        imageUrl: aiPreview.imageUrl,
        s3Key: aiPreview.s3Key,
        name: aiForm.name,
        category: aiForm.category,
        widthMeter: aiForm.widthMeter,
        heightMeter: aiForm.heightMeter,
      }, { headers });
      setShowAiForm(false);
      setAiPreview(null);
      setAiDesc('');
      setAiForm({ name: '', category: 'general', widthMeter: '2', heightMeter: '2' });
      fetchAssets();
    } catch (err) {
      alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setAiConfirming(false);
    }
  };

  // AI 모달 닫기 (미리보기 정리)
  const closeAiForm = () => {
    if (aiGenerating) return;
    if (aiPreview?.s3Key) {
      axios.post(`${API}/api/event-layout/assets/generate-discard`, { s3Key: aiPreview.s3Key }, { headers }).catch(() => {});
    }
    setShowAiForm(false);
    setAiPreview(null);
    setAiDesc('');
    setAiForm({ name: '', category: 'general', widthMeter: '2', heightMeter: '2' });
  };

  // 카테고리별 그룹핑
  const grouped = {};
  assets.forEach(a => {
    if (!grouped[a.category]) grouped[a.category] = [];
    grouped[a.category].push(a);
  });

  return (
    <div className="bg-[#0d0b14] min-h-screen">
      {/* Title Section */}
      <section className="pt-10 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-white text-2xl font-bold">에셋 관리</h1>
              {assets.length > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400">
                  전체 {assets.length}건
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Link to={`${PAPER_BASE}/layouts`} className="px-4 py-2 text-sm text-white/60 hover:text-white border border-white/10 rounded-lg transition-colors">배치도 목록</Link>
              <button onClick={() => { setShowAiForm(true); setAiDesc(''); setAiPreview(null); setAiForm({ name: '', category: 'general', widthMeter: '2', heightMeter: '2' }); }}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white text-sm rounded-lg transition-colors">
                <Sparkles size={16} /> AI 생성
              </button>
              <button onClick={openCreate} className="flex items-center gap-2 px-5 py-2.5 bg-violet-500 hover:bg-violet-600 text-white text-sm rounded-lg transition-colors">
                <Plus size={16} /> 에셋 추가
              </button>
            </div>
          </div>
          <p className="text-slate-500 text-sm">배치도에 사용할 에셋을 등록하고 관리합니다</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">

      {/* AI 생성 모달 */}
      {showAiForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={closeAiForm}>
          <div className="bg-[#1a1428] border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Sparkles size={20} className="text-fuchsia-400" />
                <h2 className="text-lg font-medium text-white">AI 에셋 생성</h2>
              </div>
              <button onClick={closeAiForm} className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>

            <div className="space-y-4">
              {/* 설명 입력 */}
              <div>
                <label className="block text-sm text-white/60 mb-1">에셋 설명</label>
                <textarea value={aiDesc} onChange={e => setAiDesc(e.target.value)} rows={2}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-fuchsia-400 resize-none"
                  placeholder="예: 몽골텐트, 빨간색 대형 무대, 흰색 접이식 의자..." autoFocus disabled={aiGenerating} />
              </div>

              {/* 생성 버튼 */}
              <button onClick={handleAiGenerate} disabled={aiGenerating || !aiDesc}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors">
                {aiGenerating ? (
                  <><Loader2 size={16} className="animate-spin" /> AI가 탑뷰 이미지를 생성하고 있습니다...</>
                ) : aiPreview ? (
                  <><RefreshCw size={16} /> 다시 생성</>
                ) : (
                  <><Sparkles size={16} /> 생성하기</>
                )}
              </button>

              {/* 미리보기 */}
              {aiPreview && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">미리보기</label>
                    <div className="flex items-center justify-center p-4 bg-[#0d0b14] rounded-lg border border-white/10">
                      <div className="w-48 h-48 rounded-lg overflow-hidden" style={{ backgroundImage: 'repeating-conic-gradient(#ffffff10 0% 25%, transparent 0% 50%)', backgroundSize: '16px 16px' }}>
                        <img src={aiPreview.imageUrl} alt="AI 생성 미리보기" className="w-full h-full object-contain" />
                      </div>
                    </div>
                    <p className="text-center text-white/30 text-xs mt-2">투명 배경 (체커보드 = 투명 영역)</p>
                  </div>

                  {/* 에셋 정보 입력 */}
                  <div>
                    <label className="block text-sm text-white/60 mb-1">이름</label>
                    <input type="text" value={aiForm.name} onChange={e => setAiForm({ ...aiForm, name: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-fuchsia-400"
                      placeholder="예: 몽골텐트" />
                  </div>

                  <div>
                    <label className="block text-sm text-white/60 mb-1">카테고리</label>
                    <select value={aiForm.category} onChange={e => setAiForm({ ...aiForm, category: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-fuchsia-400">
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-white/60 mb-1">가로 (m)</label>
                      <input type="number" step="0.1" min="0.1" value={aiForm.widthMeter} onChange={e => setAiForm({ ...aiForm, widthMeter: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-fuchsia-400" />
                    </div>
                    <div>
                      <label className="block text-sm text-white/60 mb-1">세로 (m)</label>
                      <input type="number" step="0.1" min="0.1" value={aiForm.heightMeter} onChange={e => setAiForm({ ...aiForm, heightMeter: e.target.value })}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-fuchsia-400" />
                    </div>
                  </div>

                  {/* 확정 버튼 */}
                  <button onClick={handleAiConfirm} disabled={aiConfirming || !aiForm.name}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors">
                    {aiConfirming ? (
                      <><Loader2 size={16} className="animate-spin" /> 저장 중...</>
                    ) : (
                      <><Check size={16} /> 이 이미지로 에셋 등록</>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 에셋 폼 모달 */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#1a1428] border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-white">{editId ? '에셋 수정' : '에셋 추가'}</h2>
              <button onClick={() => setShowForm(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-white/60 mb-1">이름</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400" placeholder="예: 메인무대 10x6" />
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-1">카테고리</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-white/60 mb-1">가로 (m)</label>
                  <input type="number" step="0.1" min="0.1" value={form.widthMeter} onChange={e => setForm({ ...form, widthMeter: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400" placeholder="3.0" />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-1">세로 (m)</label>
                  <input type="number" step="0.1" min="0.1" value={form.heightMeter} onChange={e => setForm({ ...form, heightMeter: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400" placeholder="3.0" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-1">기본 색상 (이미지 없을 때)</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer bg-transparent border-0" />
                  <span className="text-white/40 text-sm">{form.color}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-1">Top-View 이미지 (선택)</label>
                <input type="file" ref={fileRef} accept="image/*" onChange={handleImageChange} className="hidden" />
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => fileRef.current.click()}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white/60 text-sm hover:text-white hover:border-white/20 transition-colors">
                    <Upload size={14} /> 이미지 선택
                  </button>
                  {imagePreview && (
                    <div className="w-16 h-16 rounded-lg border border-white/10 overflow-hidden bg-white/5 flex-shrink-0">
                      <img src={imagePreview} alt="" className="w-full h-full object-contain" />
                    </div>
                  )}
                </div>
              </div>

              {/* 미리보기 */}
              {form.widthMeter && form.heightMeter && (
                <div>
                  <label className="block text-sm text-white/60 mb-1">미리보기 (비율)</label>
                  <div className="flex items-center justify-center p-4 bg-white/5 rounded-lg">
                    <div
                      style={{
                        width: `${Math.min(120, parseFloat(form.widthMeter) * 20)}px`,
                        height: `${Math.min(120, parseFloat(form.heightMeter) * 20)}px`,
                        backgroundColor: imagePreview ? 'transparent' : form.color,
                        backgroundImage: imagePreview ? `url(${imagePreview})` : 'none',
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                      }}
                      className="rounded border border-white/20 flex items-center justify-center"
                    >
                      {!imagePreview && <span className="text-white text-[10px] font-medium text-center px-1 break-all">{form.name || '에셋'}</span>}
                    </div>
                  </div>
                  <p className="text-center text-white/30 text-xs mt-1">{form.widthMeter}m × {form.heightMeter}m</p>
                </div>
              )}

              <button type="submit" disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors">
                <Save size={16} /> {saving ? '저장 중...' : editId ? '수정' : '추가'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 에셋 목록 */}
      {loading ? (
        <div className="text-center text-white/40 py-20">로딩 중...</div>
      ) : assets.length === 0 ? (
        <div className="text-center text-white/40 py-20">
          <p className="text-lg mb-2">등록된 에셋이 없습니다</p>
          <p className="text-sm">에셋을 추가하여 배치도에서 사용할 수 있습니다</p>
        </div>
      ) : (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="mb-8">
            <h2 className="text-sm font-medium text-white/60 mb-3 uppercase tracking-wider">{CATEGORY_MAP[cat] || cat}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {items.map(asset => (
                <div key={asset.id} className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-violet-400/30 transition-colors group">
                  <div className="aspect-square rounded-lg mb-3 flex items-center justify-center overflow-hidden"
                    style={{ backgroundColor: asset.imageUrl ? '#ffffff10' : (asset.color || '#6366f1') + '30' }}>
                    {asset.imageUrl ? (
                      <img src={asset.imageUrl} alt={asset.name} className="w-full h-full object-contain p-2" />
                    ) : (
                      <div className="w-3/4 h-3/4 rounded flex items-center justify-center" style={{ backgroundColor: asset.color || '#6366f1', aspectRatio: `${asset.widthMeter}/${asset.heightMeter}` }}>
                        <span className="text-white text-xs font-medium text-center px-1">{asset.name}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-white text-sm font-medium truncate">{asset.name}</p>
                  <p className="text-white/40 text-xs">{asset.widthMeter}m × {asset.heightMeter}m</p>
                  <div className="flex items-center gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEdit(asset)} className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs rounded-lg transition-colors">
                      <Pencil size={12} /> 수정
                    </button>
                    <button onClick={() => handleDelete(asset.id, asset.name)} className="flex-1 flex items-center justify-center gap-1 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs rounded-lg transition-colors">
                      <Trash2 size={12} /> 삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
      </div>
    </div>
  );
}
